import React from 'react';
import CourseTemplate from '../../components/CourseTemplate';

const BTechCSECoursePage: React.FC = () => {
  const btechCSEData = {
    title: "Bachelor of Technology in Computer Science & Engineering",
    shortName: "B.Tech CSE",
    duration: "4 Years",
    description: "A comprehensive engineering program focusing on computer science fundamentals, programming, software development, and emerging technologies like AI and machine learning.",
    overview: "B.Tech Computer Science & Engineering is one of the most popular and versatile engineering disciplines that combines mathematical rigor with practical problem-solving skills. The program covers core computer science concepts including programming languages, data structures, algorithms, software engineering, database systems, computer networks, and operating systems. Students gain expertise in emerging technologies like artificial intelligence, machine learning, blockchain, cloud computing, and cybersecurity. The curriculum is designed to produce skilled software engineers, system developers, and technology innovators who can contribute to the rapidly evolving tech industry.",
    eligibility: [
      "Completed 10+2 with Physics, Chemistry, and Mathematics",
      "Minimum 75% aggregate marks (65% for SC/ST)",
      "Must qualify JEE Main entrance examination",
      "Top colleges require JEE Advanced qualification",
      "Age: Maximum 25 years (30 for SC/ST)",
      "English proficiency required"
    ],
    careerProspects: {
      roles: [
        "Software Engineer",
        "Full Stack Developer",
        "Data Scientist",
        "Machine Learning Engineer",
        "DevOps Engineer",
        "Cybersecurity Analyst",
        "Product Manager",
        "System Architect",
        "AI Research Scientist",
        "Blockchain Developer",
        "Cloud Engineer",
        "Mobile App Developer"
      ],
      industries: [
        "Information Technology",
        "Software Development",
        "E-commerce",
        "Banking & Fintech",
        "Gaming Industry",
        "Telecommunications",
        "Healthcare Technology",
        "Automotive (Self-driving cars)",
        "Space Technology",
        "Artificial Intelligence",
        "Cybersecurity",
        "EdTech"
      ],
      averageSalary: "₹8-45 LPA (varies by company and skills)",
      topRecruiters: [
        "Google",
        "Microsoft",
        "Amazon",
        "Apple",
        "Meta (Facebook)",
        "Netflix",
        "Uber",
        "Adobe",
        "Intel",
        "NVIDIA",
        "Tesla",
        "Flipkart",
        "Zomato",
        "Paytm",
        "Tata Consultancy Services",
        "Infosys",
        "Wipro"
      ]
    },
    curriculum: [
      {
        year: 1,
        subjects: [
          "Programming Fundamentals (C/C++)",
          "Mathematics - I (Calculus)",
          "Physics",
          "Chemistry",
          "English Communication",
          "Engineering Graphics",
          "Basic Electrical Engineering",
          "Workshop Practice"
        ]
      },
      {
        year: 2,
        subjects: [
          "Data Structures and Algorithms",
          "Object-Oriented Programming (Java)",
          "Mathematics - II (Discrete Mathematics)",
          "Digital Logic Design",
          "Computer Organization",
          "Database Management Systems",
          "Statistics and Probability",
          "Environmental Science"
        ]
      },
      {
        year: 3,
        subjects: [
          "Operating Systems",
          "Computer Networks",
          "Software Engineering",
          "Web Technologies",
          "Machine Learning",
          "Compiler Design",
          "Computer Graphics",
          "Theory of Computation",
          "Microprocessors"
        ]
      },
      {
        year: 4,
        subjects: [
          "Artificial Intelligence",
          "Cybersecurity",
          "Cloud Computing",
          "Mobile App Development",
          "Blockchain Technology",
          "Big Data Analytics",
          "Capstone Project",
          "Industrial Training",
          "Elective Specializations"
        ]
      }
    ],
    admissionProcess: [
      "Appear for JEE Main examination",
      "For top NITs/IITs: Qualify JEE Advanced",
      "Register for JoSAA counseling",
      "Choice filling based on preferences",
      "Seat allotment based on rank",
      "Document verification",
      "Fee payment and admission confirmation",
      "Report to the allotted college"
    ],
    topColleges: [
      {
        name: "IIT Bombay",
        location: "Mumbai",
        fees: "₹8.5 LPA",
        ranking: "1st in India"
      },
      {
        name: "IIT Delhi",
        location: "New Delhi",
        fees: "₹8.5 LPA",
        ranking: "2nd in India"
      },
      {
        name: "IIT Madras",
        location: "Chennai",
        fees: "₹8.5 LPA",
        ranking: "3rd in India"
      },
      {
        name: "IIT Kanpur",
        location: "Kanpur",
        fees: "₹8.5 LPA",
        ranking: "4th in India"
      },
      {
        name: "IIT Kharagpur",
        location: "Kharagpur",
        fees: "₹8.5 LPA",
        ranking: "5th in India"
      },
      {
        name: "NIT Trichy",
        location: "Tiruchirappalli",
        fees: "₹5.5 LPA",
        ranking: "Top NIT"
      },
      {
        name: "BITS Pilani",
        location: "Pilani, Rajasthan",
        fees: "₹20 LPA",
        ranking: "Top Private College"
      }
    ],
    skills: [
      "Programming Languages (Python, Java, C++)",
      "Data Structures & Algorithms",
      "Web Development",
      "Database Management",
      "Software Testing",
      "Version Control (Git)",
      "Cloud Platforms (AWS, Azure)",
      "Machine Learning",
      "Problem Solving",
      "System Design",
      "API Development",
      "Cybersecurity Fundamentals"
    ],
    futureScope: "Computer Science Engineering has an extremely bright future with emerging technologies like Artificial Intelligence, Machine Learning, Quantum Computing, Internet of Things (IoT), Blockchain, and Extended Reality (AR/VR) creating new opportunities. The digital transformation across all industries ensures continuous demand for skilled CS engineers. Future prospects include roles in space technology, autonomous vehicles, smart cities, digital healthcare, fintech innovations, and green technology solutions. With the rise of remote work and global tech companies, CSE graduates have opportunities worldwide.",
    faqs: [
      {
        question: "What is the difference between B.Tech CSE and B.E. CSE?",
        answer: "B.Tech (Bachelor of Technology) is more practical and industry-oriented, while B.E. (Bachelor of Engineering) is more theoretical. However, both degrees are equivalent and offer similar career opportunities. Most modern programs are B.Tech with hands-on approach."
      },
      {
        question: "Is coding knowledge required before joining B.Tech CSE?",
        answer: "No prior coding knowledge is required. The program starts with programming fundamentals and gradually builds up your skills. However, having basic exposure to programming can be helpful."
      },
      {
        question: "What programming languages will I learn in B.Tech CSE?",
        answer: "You'll typically learn C/C++, Java, Python, JavaScript, SQL, and may also be exposed to languages like R, MATLAB, and emerging technologies based on your specialization and college curriculum."
      },
      {
        question: "Are there good placement opportunities after B.Tech CSE?",
        answer: "Yes, CSE has excellent placement opportunities with top tech companies offering packages ranging from ₹8-45 LPA for fresh graduates. The demand for skilled programmers continues to grow across all industries."
      },
      {
        question: "Can I pursue higher studies after B.Tech CSE?",
        answer: "Absolutely! You can pursue M.Tech, MS abroad, MBA, or specialized certifications. Many graduates also opt for research programs leading to PhD in computer science or related fields."
      },
      {
        question: "What are the emerging specializations in CSE?",
        answer: "Popular specializations include Artificial Intelligence & Machine Learning, Cybersecurity, Data Science, Cloud Computing, Blockchain Technology, Internet of Things (IoT), and Computer Vision."
      }
    ]
  };

  return (
    <div className={`min-h-screen font-[Inter,Poppins,sans-serif] antialiased bg-gradient-to-br from-[#0a0a1f] via-[#14142b] to-[#1e1e2f] text-white`} style={{ transition: 'background 0.5s, color 0.5s' }}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <h1 className="text-4xl md:text-5xl font-extrabold mb-8 text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 via-pink-400 to-purple-400 tracking-tight glow-text drop-shadow-glow text-center">
          {btechCSEData.title}
        </h1>
        <CourseTemplate courseData={btechCSEData} />
      </div>
    </div>
  );
};

export default BTechCSECoursePage;
